import { useEffect, useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useReducedMotion } from "framer-motion";
import {
  IconArrowUp,
  IconFileSpreadsheet,
  IconPhotoPlus,
  IconPlayerStop,
  IconX,
} from "@tabler/icons-react";
import { HoverInfo } from "@/components/HoverInfo/HoverInfo";
import { useAiAvailabilityStore } from "@/lib/ai/ai-store";
import {
  ACCEPTED_IMAGE_TYPES,
  ACCEPTED_SPREADSHEET_EXTENSIONS,
  ACCEPTED_SPREADSHEET_TYPES,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  attachableFilesFrom,
  attachmentKind,
  filesToImageAttachments,
  imageFilesFrom,
  validateFiles,
  type ImageAttachment,
  type RejectedFile,
} from "@/lib/ai/attachments";
import { AI_MESSAGE_COUNT_FROM, MAX_AI_MESSAGE_CHARS } from "@/lib/ai/messageLimit";

/**
 * Shared composer for both surfaces — a ChatGPT/Claude-style rounded "pill":
 * a borderless auto-growing textarea inside a card that owns the focus ring
 * (focus-within), a circular attach button, an attachments row of image
 * thumbnails, and a drag-and-drop zone. Images attach via paste, drop, or the
 * picker; they're validated (type/size/count), previewed via object URLs
 * (minted/revoked in the store — no leaks), and sent as base64 blocks.
 *
 * Reads/writes the cross-shell store draft + attachments (the panel unmounts
 * with its shell — local state would be eaten). Both clear ONLY on an accepted
 * send: the provider's in-flight/scope guards are stricter than `streaming`,
 * and a silent rejection must not eat typed text or staged images — onSend
 * returns the provider verdict.
 *
 * Both surfaces share ONE send affordance: Enter = send, Shift+Enter = newline,
 * no send button; a Stop button shows only while streaming (Enter can't abort a
 * stream). The builder panel just differs by placeholder + test id.
 */
type ChatProps = {
  variant: "chat";
  streaming: boolean;
  onSend: (text: string, images?: ImageAttachment[]) => boolean;
  onStop: () => void;
  placeholder: string;
  ariaLabel: string;
  autoFocus?: boolean;
};
type BuilderProps = {
  variant: "builder";
  streaming: boolean;
  onSend: (text: string, images?: ImageAttachment[]) => boolean;
  onStop: () => void;
  placeholder: string;
  ariaLabel: string;
  autoFocus?: boolean;
  /**
   * Characters `onSend` appends before the text reaches the backend (the
   * builder's canvas directive). They count against the same 8000-character
   * cap, so the composer reserves them instead of letting a draft that looks
   * fine come back as a 400 after it was cleared.
   */
  reservedChars?: number;
};

const MAX_ATTACHMENT_MB = Math.round(MAX_ATTACHMENT_BYTES / (1024 * 1024));

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function Composer(props: ChatProps | BuilderProps) {
  const t = useTranslations("aiAssistant");
  const reduceMotion = useReducedMotion();
  const draft = useAiAvailabilityStore((s) => s.composerDraft);
  const attachments = useAiAvailabilityStore((s) => s.composerAttachments);
  const removeAttachment = useAiAvailabilityStore((s) => s.removeComposerAttachment);
  const setDraft = (v: string) => useAiAvailabilityStore.getState().setComposerDraft(v);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Guards the async base64 read so a double Enter can't fire two sends.
  const submittingRef = useRef(false);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  const builder = props.variant === "builder";
  const lengthId = useId();

  useEffect(() => {
    if (props.autoFocus) inputRef.current?.focus();
  }, [props.autoFocus]);

  // Auto-grow with the draft; max-h caps it and overflow takes over beyond.
  // scrollHeight EXCLUDES the border, so add the border-box delta back to avoid
  // a phantom 2px scrollbar. Also re-measure on WIDTH changes (a ResizeObserver):
  // when the panel mounts mid-animation (e.g. the builder rail growing from 0),
  // the first measure happens at a tiny width where the placeholder wraps to
  // many lines and locks in a too-tall box — re-running once the width settles
  // fixes it. Guarded on width so our own height write doesn't loop.
  // The placeholder is a dep too: with an empty draft it IS the measured text
  // (a create-event step answered by a button swaps it for a longer line).
  const { placeholder } = props;
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const fit = () => {
      el.style.height = "auto";
      const borders = el.offsetHeight - el.clientHeight;
      const next = el.scrollHeight + borders;
      el.style.height = `${next}px`;
      el.style.overflowY = next > 160 ? "auto" : "hidden";
    };
    fit();
    let lastWidth = el.clientWidth;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      if (w !== lastWidth) {
        lastWidth = w;
        fit();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [draft, placeholder]);

  // A spreadsheet is NOT sent as a content block — without a note the model
  // wouldn't know a file is attached, and a sheet-only turn (no text, no
  // image) would be dropped by the provider's text-or-image guard. Append a
  // short note so the turn carries content the model can act on.
  const withSheetNote = (text: string, pending: typeof attachments): string => {
    const sheetFiles = pending.filter((a) => attachmentKind(a.file) === "spreadsheet");
    if (!sheetFiles.length) return text;
    const names = sheetFiles.map((a) => a.file.name).join(", ");
    const note = t.has("attachedSheetNote")
      ? t("attachedSheetNote", { names })
      : `Attached spreadsheet: ${names}`;
    return text ? `${text}\n\n${note}` : note;
  };

  const trimmed = draft.trim();
  const canSend = trimmed !== "" || attachments.length > 0;
  const atLimit = attachments.length >= MAX_ATTACHMENTS;
  // What the backend counts against its cap: the trimmed draft plus the
  // spreadsheet note, exactly as submit() builds it.
  const reservedChars = props.variant === "builder" ? (props.reservedChars ?? 0) : 0;
  const outgoingLength = withSheetNote(trimmed, attachments).length + reservedChars;
  const showLength = outgoingLength >= AI_MESSAGE_COUNT_FROM;
  const tooLong = outgoingLength > MAX_AI_MESSAGE_CHARS;

  const describeRejections = (rejected: RejectedFile[]): string => {
    const seen = new Set<string>();
    for (const r of rejected) {
      const msg =
        r.kind === "tooMany"
          ? t("attachTooMany", { max: MAX_ATTACHMENTS })
          : r.kind === "tooLarge"
            ? t("attachTooLarge", { name: r.name, max: MAX_ATTACHMENT_MB })
            : t("attachBadType", { name: r.name });
      seen.add(msg);
    }
    return Array.from(seen).join(" ");
  };

  const addFiles = (files: File[]) => {
    if (files.length === 0) return;
    const existing = useAiAvailabilityStore.getState().composerAttachments.length;
    const { accepted, rejected } = validateFiles(files, existing);
    if (accepted.length) {
      useAiAvailabilityStore.getState().addComposerAttachments(accepted);
      setAttachError(null);
    }
    if (rejected.length) setAttachError(describeRejections(rejected));
  };

  const submit = async () => {
    if (props.streaming || submittingRef.current || !canSend) return;
    submittingRef.current = true;
    try {
      const text = useAiAvailabilityStore.getState().composerDraft.trim();
      const pending = useAiAvailabilityStore.getState().composerAttachments;
      const outgoing = withSheetNote(text, pending);
      // Over the backend cap the send would come back as a 400 after the draft
      // was already cleared. Keep the draft; the red count under the input
      // already says why.
      if (outgoing.length + reservedChars > MAX_AI_MESSAGE_CHARS) return;
      // Only IMAGES become base64 content blocks for the model. Spreadsheets
      // ride along solely via turnAttachments (parsed in-browser by the import
      // client tools) — never inlined to the model.
      const imageFiles = pending.filter((a) => attachmentKind(a.file) === "image");
      let images: Awaited<ReturnType<typeof filesToImageAttachments>> | undefined;
      try {
        images = imageFiles.length
          ? await filesToImageAttachments(imageFiles.map((a) => a.file))
          : undefined;
      } catch {
        // filesToImageAttachments can reject (attachTooLarge backstop, read
        // failure) — without this catch the send would silently do NOTHING
        // (unhandled rejection, attachments stuck). Surface it instead.
        setAttachError(
          t("attachTooLarge", { name: imageFiles[0]?.file.name ?? "", max: MAX_ATTACHMENT_MB }),
        );
        return;
      }
      const accepted = props.onSend(outgoing, images);
      if (accepted) {
        setDraft("");
        // Stamp the sent Files for this turn BEFORE clearing the composer —
        // the in-builder upload tool may need the bytes later in the same turn.
        // (Empty list when nothing was attached, which also clears stale ones.)
        useAiAvailabilityStore.getState().setTurnAttachments(pending.map((a) => a.file));
        useAiAvailabilityStore.getState().clearComposerAttachments();
        setAttachError(null);
      }
    } finally {
      submittingRef.current = false;
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = imageFilesFrom(e.clipboardData);
    if (files.length) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    // Reset so re-picking the same file fires change again.
    e.target.value = "";
  };

  const hasFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer?.types ?? []).includes("Files");

  const onDragEnter = (e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (hasFiles(e)) e.preventDefault(); // allow the drop
  };
  const onDragLeave = (e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    addFiles(attachableFilesFrom(e.dataTransfer));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Same on both surfaces: Enter sends, Shift+Enter = newline.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };

  const attachButton = (
    <button
      type="button"
      data-testid="ai-attach"
      aria-label={t("attach")}
      disabled={atLimit}
      onClick={() => fileInputRef.current?.click()}
      // Sized to mirror the 26px square send button so the two controls sit as
      // bottom-aligned peers on either side of the input — ghost/muted here vs.
      // the filled send, keeping send the primary action. before:-inset-1.5
      // restores a comfortable tap target without growing the visual box.
      className="relative flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md font-tertiary hover:surface-tertiary hover:font-secondary transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-default before:absolute before:-inset-1.5 before:content-['']"
    >
      <IconPhotoPlus size={16} />
    </button>
  );

  const textarea = (
    <textarea
      ref={inputRef}
      data-testid={builder ? "ai-builder-input" : "ai-assistant-input"}
      aria-label={props.ariaLabel}
      rows={1}
      placeholder={props.placeholder}
      className="min-h-[21px] flex-1 resize-none overflow-hidden bg-transparent border-0 px-1.5 py-0 text-sm leading-[1.5] max-h-40 placeholder:font-tertiary focus:outline-none focus:ring-0"
      value={draft}
      aria-describedby={showLength ? lengthId : undefined}
      aria-invalid={tooLong || undefined}
      onChange={(e) => setDraft(e.target.value)}
      onPaste={onPaste}
      onKeyDown={onKeyDown}
    />
  );

  return (
    <div className="shrink-0 border-t outline-quaternary safe-area-bottom">
      <div className="px-3 py-2.5">
        <input
          ref={fileInputRef}
          type="file"
          accept={[
            ...ACCEPTED_IMAGE_TYPES,
            ...ACCEPTED_SPREADSHEET_TYPES,
            ...ACCEPTED_SPREADSHEET_EXTENSIONS,
          ].join(",")}
          multiple
          className="hidden"
          onChange={onPick}
          tabIndex={-1}
          aria-hidden="true"
        />
        <div
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={`relative rounded-[10px] border surface-secondary ${
            reduceMotion ? "" : "transition-shadow"
          } focus-within:ring-2 focus-within:ring-(--color-outline-focus) focus-within:shadow-sm ${
            dragging
              ? "border-dashed ring-2 ring-(--color-outline-focus)"
              : "outline-quaternary"
          }`}
        >
          {dragging && (
            <div
              aria-hidden="true"
              className="absolute inset-0 z-10 flex items-center justify-center rounded-[10px] border border-dashed outline-focus surface-tertiary text-xs font-medium pointer-events-none"
            >
              {t("attachDrop")}
            </div>
          )}

          {attachments.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 px-2 pt-2">
              {attachments.map((a) => (
                <div key={a.id} className="group/att relative shrink-0">
                  {attachmentKind(a.file) === "image" ? (
                    /* Object URL from the user's own File only; revoked on remove/clear. */
                    <img
                      src={a.url}
                      alt={t("imageAlt", { name: a.file.name })}
                      title={`${a.file.name} · ${formatSize(a.file.size)}`}
                      className="h-14 w-14 rounded-lg object-cover border outline-quaternary"
                    />
                  ) : (
                    /* Spreadsheets have no preview — show a labelled file chip. */
                    <div
                      title={`${a.file.name} · ${formatSize(a.file.size)}`}
                      className="flex h-14 w-14 flex-col items-center justify-center gap-1 rounded-lg border outline-quaternary surface-tertiary px-1"
                    >
                      <IconFileSpreadsheet size={20} className="font-secondary" />
                      <span className="w-full truncate text-center text-[10px] font-tertiary">
                        {a.file.name}
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid="ai-attach-remove"
                    aria-label={t("attachRemove", { name: a.file.name })}
                    onClick={() => removeAttachment(a.id)}
                    className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full surface-primary border outline-quaternary shadow-sm cursor-pointer before:absolute before:-inset-2 before:content-['']"
                  >
                    <IconX size={12} />
                  </button>
                </div>
              ))}
              <span className="ml-auto self-center pr-1 text-[11px] font-tertiary tabular-nums">
                {attachments.length}/{MAX_ATTACHMENTS}
              </span>
            </div>
          )}

          {attachError && (
            <div role="alert" className="px-3 pt-2 text-xs font-error">
              {attachError}
            </div>
          )}

          {/* Screen readers hear the cap once, when the draft crosses it —
              the visible count changes on every keystroke and would be read
              out letter by letter. Mounted always, so the first announcement
              is not lost to a region that appears together with its text. */}
          <div className="sr-only" aria-live="polite" data-testid="ai-message-length-live">
            {tooLong ? t("errors.messageTooLong") : ""}
          </div>
          {showLength && (
            <div
              data-testid="ai-message-length"
              id={lengthId}
              className={`px-3 pt-2 text-xs tabular-nums ${tooLong ? "font-error" : "font-tertiary"}`}
            >
              {tooLong
                ? t("messageTooLongDraft", { count: outgoingLength, max: MAX_AI_MESSAGE_CHARS })
                : `${outgoingLength}/${MAX_AI_MESSAGE_CHARS}`}
            </div>
          )}

          {/* One shared layout for chat + builder. Enter still sends
              (Shift+Enter = newline); the right slot holds the help-center
              style square 26px send button (idle) which swaps to a same-sized
              Stop while generating, since Enter can't abort an in-flight
              stream. Send is disabled until there's text or an attachment. */}
          <div className="flex items-center gap-1.5 py-2 px-2">
            {attachButton}
            {textarea}
            {props.streaming ? (
              <HoverInfo message={t("stop")} className="shrink-0">
                <button
                  type="button"
                  data-testid={builder ? "ai-builder-stop" : undefined}
                  aria-label={t("stop")}
                  className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md cta-primary font-light-primary cursor-pointer transition-colors"
                  onClick={props.onStop}
                >
                  <IconPlayerStop size={14} />
                </button>
              </HoverInfo>
            ) : (
              <button
                type="button"
                data-testid={builder ? "ai-builder-send" : "ai-assistant-send"}
                aria-label={t("send")}
                // Solid at all times like the help-center send (it only dims
                // while sending — which here is the Stop state). submit() no-ops
                // on an empty draft, so no disabled/grey state is needed.
                onClick={() => void submit()}
                className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md cta-primary font-light-primary cursor-pointer transition-colors hover:cta-hover"
              >
                <IconArrowUp size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
